import {
  ErrorResponse,
  INavigator,
  InMemoryWebStorage,
  IWindow,
  NavigateParams,
  NavigateResponse,
  StateStore,
  UserManager,
  WebStorageStateStore,
} from "oidc-client-ts";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { IDP_CALLBACK_PATH, IdPErrorCode } from "./constants";
import {
  ID_TOKEN_LEEWAY_SECONDS,
  idTokenExpiry,
  idTokenIssuer,
  validateIdTokenClaims,
} from "./idTokenClaims";
import { IdPIssuerConfig } from "./issuers";
import {
  AppState,
  IdPClientConfig,
  IdPClientInterface,
  IdPClientRootOptions,
  IdPClientState,
  IdPLogoutOptions,
  IdPRedirectError,
  IdPRedirectLoginOptions,
  IdPRedirectResult,
  IdPTokens,
  IdPUser,
} from "./types";

/** The scope auth0-spa-js sent. No `offline_access`: there are no refresh tokens. */
const SCOPE = "openid profile email";

/** The OIDC state entries' prefix in sessionStorage. */
export const STATE_STORE_PREFIX = "oidc.";

/** The error code and description auth0-spa-js gave a callback that matches no login. */
export const MISSING_TRANSACTION = { error: "missing_transaction", description: "Invalid state" };

/**
 * A callback the client refused. Mapped to `IdPRedirectError` by `toIdPRedirectError`.
 */
export class IdPCallbackError extends Error {
  constructor(
    readonly error: string,
    readonly errorDescription: string,
    readonly appState?: AppState
  ) {
    super(errorDescription);
    this.name = "IdPCallbackError";
  }
}

/**
 * Sends the browser to the authorize URL and resolves, like auth0-spa-js did. oidc-client-ts's
 * own navigator only resolves on a later `pageshow`.
 */
class AssignNavigator implements INavigator {
  prepare(): Promise<IWindow> {
    return Promise.resolve({
      navigate: (params: NavigateParams): Promise<NavigateResponse> => {
        window.location.assign(params.url);
        return Promise.resolve({ url: params.url });
      },
      close: () => {},
    });
  }

  callback(): Promise<void> {
    return Promise.resolve();
  }
}

const randomNonce = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

const definedOnly = (params: Record<string, string | undefined>): Record<string, string> =>
  Object.fromEntries(
    Object.entries(params).filter((entry): entry is [string, string] => entry[1] !== undefined)
  );

interface Issuer {
  config: IdPIssuerConfig;
  manager: UserManager;
}

/** Timeout for discovery and token requests; auth0-spa-js's was 10 s. */
const REQUEST_TIMEOUT_SECONDS = 10;

/**
 * Static metadata when the issuer's endpoints are all configured (Auth0), so no discovery request
 * is made; otherwise discovery, with the configured endpoints overriding it.
 */
const metadataSettings = (issuer: IdPIssuerConfig) => {
  const endpoints = definedOnly({
    authorization_endpoint: issuer.authorizationEndpoint,
    token_endpoint: issuer.tokenEndpoint,
    end_session_endpoint: issuer.endSessionEndpoint,
  });
  return issuer.authorizationEndpoint && issuer.tokenEndpoint
    ? { metadata: { issuer: issuer.issuer, ...endpoints } }
    : { metadataSeed: endpoints };
};

/**
 * Implements IdPClientInterface with oidc-client-ts: one UserManager per trusted issuer, sharing
 * one sessionStorage state store. It is the only file in the app that imports oidc-client-ts.
 *
 * Tokens are held in memory only. Every sign-in sends PKCE S256, `state` and `nonce`. There is
 * no silent renew, session monitor, refresh token or iframe.
 */
export class OidcIdPClient implements IdPClientInterface {
  private readonly issuers: Issuer[];
  private readonly stateStore: StateStore;
  private readonly audience: string;
  private callback?: { url: string; result: Promise<IdPRedirectResult> };
  private readonly signedOutListeners = new Set<() => void>();

  constructor(config: IdPClientConfig, stateStorage: Storage = window.sessionStorage) {
    if (config.issuers.length === 0) {
      throw new Error("At least one IdP issuer must be configured");
    }
    const ids = config.issuers.map((issuer) => issuer.issuer);
    if (new Set(ids).size !== ids.length) {
      throw new Error(`IdP issuers must be unique: ${ids.join(", ")}`);
    }
    this.audience = config.audience;
    this.stateStore = new WebStorageStateStore({ store: stateStorage, prefix: STATE_STORE_PREFIX });
    this.issuers = config.issuers.map((issuer) => ({
      config: issuer,
      manager: new UserManager(
        {
          authority: issuer.issuer,
          client_id: issuer.clientId,
          redirect_uri: config.redirectUri,
          response_type: "code",
          response_mode: "query",
          scope: SCOPE,
          ...metadataSettings(issuer),
          requestTimeoutInSeconds: REQUEST_TIMEOUT_SECONDS,
          stateStore: this.stateStore,
          userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
          automaticSilentRenew: false,
          monitorSession: false,
          loadUserInfo: false,
          // Removes this client's pending logins older than this when a new one starts.
          staleStateAgeInSeconds: 15 * 60,
        },
        new AssignNavigator()
      ),
    }));
  }

  private get defaultIssuer(): Issuer {
    return this.issuers[0];
  }

  private findIssuer(iss: string | undefined): Issuer | undefined {
    return this.issuers.find((issuer) => issuer.config.issuer === iss);
  }

  async loginWithRedirect(opts: IdPRedirectLoginOptions): Promise<void> {
    const { appState, authorizationParams } = opts;
    const { redirect_uri, login_hint, prompt, audience, ...extra } = authorizationParams;
    // Every login starts at the first issuer's authorize endpoint (the Twilio proxy), which
    // picks the IdP. The callback's iss says which one answered.
    await this.defaultIssuer.manager.signinRedirect({
      state: appState,
      nonce: randomNonce(),
      redirect_uri,
      login_hint,
      prompt,
      extraQueryParams: definedOnly({ audience: audience ?? this.audience, ...extra }),
    });
  }

  /** Completes the callback at most once per URL, so StrictMode's second effect doesn't redo it. */
  handleRedirectCallbackOnce(url: string): Promise<IdPRedirectResult> {
    if (this.callback?.url !== url) {
      this.callback = { url, result: this.handleRedirectCallback(url) };
    }
    return this.callback.result;
  }

  async handleRedirectCallback(url: string = window.location.href): Promise<IdPRedirectResult> {
    const params = new URL(url).searchParams;
    const iss = params.get("iss");
    const stateId = params.get("state");
    // RFC 9207: no iss means the first issuer (Auth0 production doesn't send one yet).
    const issuer = iss === null ? this.defaultIssuer : this.findIssuer(iss);
    if (!issuer) {
      // The login can't be finished anywhere, so its state is dropped too.
      if (stateId) {
        await this.stateStore.remove(stateId);
      }
      throw new IdPCallbackError(
        IdPErrorCode.UNKNOWN_ERROR,
        `The callback's issuer (iss) "${iss}" is not trusted`
      );
    }
    if (stateId) {
      await this.moveStateTo(stateId, issuer);
    }

    let user;
    try {
      user = await issuer.manager.signinRedirectCallback(url);
    } catch (error: unknown) {
      throw toCallbackError(error);
    }
    try {
      if (!user.access_token || !user.id_token) {
        throw new Error("The token response is missing the access token or the ID token");
      }
      validateIdTokenClaims(user.id_token, {
        issuer: issuer.config.issuer,
        clientId: issuer.config.clientId,
      });
    } catch (error: unknown) {
      await issuer.manager.removeUser();
      throw new IdPCallbackError(IdPErrorCode.UNKNOWN_ERROR, (error as Error).message);
    }
    return {
      appState: user.state as AppState | undefined,
      issuer: issuer.config.issuer,
      user: user.profile as IdPUser,
    };
  }

  /**
   * A login starts at the first issuer, so its state names that issuer. When the proxy routed it
   * to another trusted issuer, the state is moved there so that issuer's UserManager accepts it.
   * The PKCE verifier, nonce and app state are unchanged.
   */
  private async moveStateTo(stateId: string, issuer: Issuer): Promise<void> {
    const stored = await this.stateStore.get(stateId);
    if (!stored) {
      return;
    }
    const state = JSON.parse(stored) as { authority?: string; client_id?: string };
    if (state.authority === issuer.config.issuer && state.client_id === issuer.config.clientId) {
      return;
    }
    // Only a state this client started is moved.
    if (!this.findIssuer(state.authority)) {
      return;
    }
    state.authority = issuer.config.issuer;
    state.client_id = issuer.config.clientId;
    await this.stateStore.set(stateId, JSON.stringify(state));
  }

  private async heldIssuer(): Promise<{ issuer: Issuer; tokens: IdPTokens } | undefined> {
    for (const issuer of this.issuers) {
      const user = await issuer.manager.getUser();
      if (!user?.access_token || !user.id_token) {
        continue;
      }
      const { expires_in } = user;
      // Tokens about to expire count as none, like auth0-spa-js's cache; the ID token's own exp too.
      if (expires_in !== undefined && expires_in <= ID_TOKEN_LEEWAY_SECONDS) {
        continue;
      }
      const idTokenExp = idTokenExpiry(user.id_token);
      if (idTokenExp === undefined || idTokenExp - Date.now() / 1000 <= ID_TOKEN_LEEWAY_SECONDS) {
        continue;
      }
      return {
        issuer,
        tokens: { access_token: user.access_token, id_token: user.id_token, expires_in },
      };
    }
    return undefined;
  }

  /** Calls `listener` whenever `logout` clears the tokens. Returns the unsubscribe function. */
  onSignedOut(listener: () => void): () => void {
    this.signedOutListeners.add(listener);
    return () => {
      this.signedOutListeners.delete(listener);
    };
  }

  async getHeldTokens(): Promise<IdPTokens | undefined> {
    return (await this.heldIssuer())?.tokens;
  }

  async logout(opts: IdPLogoutOptions): Promise<void> {
    const { returnTo, idTokenHint, openUrl } = opts;
    const held = await this.heldIssuer();
    const issuer =
      held?.issuer ??
      (idTokenHint ? this.findIssuer(idTokenIssuer(idTokenHint)) : undefined) ??
      this.defaultIssuer;
    await Promise.all(this.issuers.map(({ manager }) => manager.removeUser()));
    if (openUrl === false) {
      // Only when staying on the page: a re-render mid-redirect could start a silent login.
      this.signedOutListeners.forEach((listener) => listener());
      return;
    }
    let endpoint = issuer.config.endSessionEndpoint;
    if (!endpoint) {
      try {
        endpoint = await issuer.manager.metadataService.getEndSessionEndpoint();
      } catch (error: unknown) {
        console.error(`${issuer.config.issuer} discovery failed; skipping IdP logout`, error);
      }
    }
    let url = returnTo;
    if (endpoint) {
      const logoutUrl = new URL(endpoint);
      logoutUrl.searchParams.set("client_id", issuer.config.clientId);
      logoutUrl.searchParams.set(issuer.config.logoutReturnParam, returnTo);
      // Only OIDC RP-initiated logout reads id_token_hint; Auth0's /v2/logout would just log it.
      const hint = idTokenHint ?? held?.tokens.id_token;
      if (hint && issuer.config.logoutReturnParam === "post_logout_redirect_uri") {
        logoutUrl.searchParams.set("id_token_hint", hint);
      }
      url = logoutUrl.href;
    } else {
      console.error(`${issuer.config.issuer} has no end-session endpoint; skipping IdP logout`);
    }
    if (openUrl) {
      await openUrl(url);
    } else {
      window.location.assign(url);
    }
  }
}

const toCallbackError = (error: unknown): IdPCallbackError => {
  if (error instanceof IdPCallbackError) {
    return error;
  }
  if (error instanceof ErrorResponse) {
    // An ?error= callback, or the token endpoint's error. The former carries the app state.
    return new IdPCallbackError(
      error.error ?? IdPErrorCode.UNKNOWN_ERROR,
      error.error_description ?? error.message,
      error.state as AppState | undefined
    );
  }
  const message = error instanceof Error ? error.message : String(error);
  if (message === "No matching state found in storage") {
    // Back, an old tab or a replayed callback; the identifier page shows "session ended" for it.
    return new IdPCallbackError(MISSING_TRANSACTION.error, MISSING_TRANSACTION.description);
  }
  // Not an OAuth error: e.g. a bad nonce or a failed request.
  return new IdPCallbackError(IdPErrorCode.UNKNOWN_ERROR, message);
};

/**
 * Maps a callback failure to `IdPRedirectError`.
 */
export const toIdPRedirectError = (error: unknown): IdPRedirectError => {
  const { error: code, errorDescription, appState } = toCallbackError(error);
  const redirectError: IdPRedirectError = { error: code };
  if (errorDescription) {
    redirectError.errorDescription = errorDescription;
  }
  if (appState && typeof appState === "object") {
    redirectError.appState = appState;
  }
  return redirectError;
};

/**
 * Whether the URL's query is an IdP callback: `code` or `error`, plus `state`.
 */
export const isIdPCallbackSearch = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return (!!params.get("code") || !!params.get("error")) && !!params.get("state");
};

const isIdPCallbackLocation = (): boolean =>
  window.location.pathname === IDP_CALLBACK_PATH && isIdPCallbackSearch(window.location.search);

const IdPClientStateContext = createContext<IdPClientState | null>(null);

/**
 * Creates the client and, on a callback page load, completes the callback once and reports it
 * through `onRedirectCallback` or `onRedirectError`.
 */
export const IdPClientRoot = ({
  children,
  issuers,
  audience,
  redirectUri,
  onRedirectCallback,
  onRedirectError,
}: IdPClientRootOptions): JSX.Element => {
  // The config is fixed for the page's lifetime.
  const [client] = useState(() => new OidcIdPClient({ issuers, audience, redirectUri }));
  const loadedAsCallback = useRef(isIdPCallbackLocation());
  const [session, setSession] = useState<Omit<IdPClientState, "client">>({
    isAuthenticated: false,
    isLoading: loadedAsCallback.current,
  });
  const handlers = useRef({ onRedirectCallback, onRedirectError });
  handlers.current = { onRedirectCallback, onRedirectError };

  useEffect(
    () =>
      client.onSignedOut(() => {
        setSession({ isAuthenticated: false, isLoading: false });
      }),
    [client]
  );

  useEffect(() => {
    if (!loadedAsCallback.current) {
      return;
    }
    let active = true;
    client.handleRedirectCallbackOnce(window.location.href).then(
      ({ appState, user }) => {
        if (!active) return;
        setSession({ isAuthenticated: true, isLoading: false, user });
        try {
          handlers.current.onRedirectCallback(appState, user);
        } catch (error: unknown) {
          handlers.current.onRedirectError(toIdPRedirectError(error));
        }
      },
      (error: unknown) => {
        if (!active) return;
        setSession({ isAuthenticated: false, isLoading: false });
        handlers.current.onRedirectError(toIdPRedirectError(error));
      }
    );
    return () => {
      active = false;
    };
  }, [client]);

  const value = useMemo(() => ({ client, ...session }), [client, session]);
  return <IdPClientStateContext.Provider value={value}>{children}</IdPClientStateContext.Provider>;
};

/**
 * Returns the IdP client and the IdP session state. Must be rendered inside IdPClientRoot.
 */
export function useIdPClient(): IdPClientState {
  const state = useContext(IdPClientStateContext);
  if (!state) {
    throw new Error("useIdPClient must be rendered inside IdPClientRoot");
  }
  return state;
}
